import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Endpoints de « J'aime » public par module
const ENDPOINT = {
  annonce: (id) => `/annonces/${id}/like`,
  voisin: (id) => `/entre-voisins/${id}/like`,
};

// Bouton « J'aime » public : compteur visible par tous, réservé aux membres connectés.
// variant="button" (fiches détail) ou variant="icon" (cartes de liste, dans un <Link>).
export default function LikeButton({
  type,
  id,
  liked: likedInit = false,
  count: countInit = 0,
  variant = "button",
  testId = "like-button",
  className = "",
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [liked, setLiked] = useState(likedInit);
  const [count, setCount] = useState(countInit);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setLiked(likedInit); }, [likedInit]);
  useEffect(() => { setCount(countInit); }, [countInit]);

  const toggle = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (busy) return;
    if (!user || user === false) {
      toast.message("Connectez-vous pour aimer cette publication.");
      navigate("/connexion");
      return;
    }
    setBusy(true);
    const next = !liked;
    setLiked(next);
    setCount((c) => Math.max(0, c + (next ? 1 : -1)));
    try {
      const { data } = await api.post(ENDPOINT[type](id));
      setLiked(data.liked);
      setCount(data.likes);
    } catch {
      setLiked(!next);
      setCount((c) => Math.max(0, c + (next ? -1 : 1)));
      toast.error("Action impossible");
    } finally {
      setBusy(false);
    }
  };

  if (variant === "icon") {
    return (
      <button
        type="button"
        onClick={toggle}
        data-testid={testId}
        aria-label={liked ? "Je n'aime plus" : "J'aime"}
        className={`inline-flex items-center gap-1 text-xs font-semibold transition-colors ${liked ? "text-rose-600" : "text-slate-500 hover:text-rose-600"} ${className}`}
      >
        <Heart size={14} className={liked ? "fill-rose-500 text-rose-500" : ""} /> {count}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={toggle}
      data-testid={testId}
      className={`inline-flex items-center justify-center h-9 px-3 rounded-md border text-sm font-medium transition-colors ${liked ? "text-rose-600 border-rose-300 bg-rose-50" : "text-slate-700 border-slate-200 bg-white hover:bg-slate-50"} ${className}`}
    >
      <Heart size={15} className={`mr-1.5 ${liked ? "fill-rose-500 text-rose-500" : ""}`} /> J'aime
      {count > 0 && <span className="ml-1.5 text-xs opacity-80">{count}</span>}
    </button>
  );
}
